import { atom, useAtom } from "jotai";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { notify } from "../util/notify";
import axios from "axios";
import { UserType, toSlug } from "common";
import { useEffect } from "react";

export const initialUserState = {
  username: "",
  email: "",
  group: "",
  type: UserType.Regular,
  facilities: [],
  authToken: "",
};

export const GLOBAL_TOAST_ID = "global-toast-id";

const loadedUserData = localStorage.getItem("userState")
  ? JSON.parse(localStorage.getItem("userState") as string)
  : initialUserState;

export const userState = atom(loadedUserData);

let refreshAuthTokenCalled = false;
export const useUser = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useAtom(userState);
  const { group, facility, payer, residentName, billType, index } = params;
  const clearSession = () => {
    setUser(initialUserState);
    localStorage.removeItem("userState");
  };

  useEffect(() => {
    if (user?.username && user?.authToken) {
      localStorage.setItem("userState", JSON.stringify(user));
    } else {
      localStorage.removeItem("userState");
    }
  }, [user]);

  if (location.pathname === "/") {
    window.location.href = "/fairview-and-co";
  }

  useEffect(() => {
    if (
      user?.username &&
      user?.authToken &&
      !refreshAuthTokenCalled &&
      !location.pathname.includes("login") &&
      !location.pathname.includes("reset-password")
    ) {
      refreshAuthTokenCalled = true;
      invokeApi({
        endpoint: "/refreshAuthToken",
        callingRefreshAuthToken: true,
      });
    }
  }, []);

  const invokeApi = async ({
    endpoint,
    data,
    callingRefreshAuthToken,
  }: any) => {
    const toastId = notify.loading("Loading...", { id: GLOBAL_TOAST_ID });
    const requestAuthToken = user?.authToken;

    try {
      const response = await axios({
        method: "post",
        url: process.env.REACT_APP_API_URL + endpoint,
        data: {
          authToken: requestAuthToken,
          group,
          facility,
          payer,
          residentName,
          billType,
          index: index !== undefined ? Number(index) : index,
          ...data,
        },
      });
      if (response.data.authToken) {
        setUser((currentUser: { username?: string; authToken?: string; [key: string]: unknown }) =>
          currentUser?.username && currentUser.authToken === requestAuthToken
            ? { ...currentUser, authToken: response.data.authToken }
            : currentUser
        );
      }

      return response.data;
    } catch (error: any) {
      if (callingRefreshAuthToken) {
        clearSession();
        const next = location.pathname.includes("login")
          ? `/${params.group}`
          : `${location.pathname}${location.search}`;
        navigate(`/${toSlug(params.group)}/login?next=${encodeURIComponent(next)}`);
        return;
      }

      console.error(error?.response?.data?.description || error);
      console.error(error?.response?.data?.message);

      const description = error?.response?.data?.description;
      if (description === "Custom Error") {
        notify.error(error?.response?.data?.message, { duration: 5000 });
      } else if (description === "Authentication Error") {
        clearSession();
        notify.error("Please log in", { id: GLOBAL_TOAST_ID });
        const next = `${location.pathname}${location.search}`;
        navigate(`/${toSlug(params.group)}/login?next=${encodeURIComponent(next)}`);
      } else if (description === "Validation Error") {
        const validationError = JSON.parse(error.response.data.message);
        notify.error(
          validationError
            .map(
              ({ instancePath, message }: any) =>
                `${instancePath.substring(1)}: ${message}`
            )
            .join("<br/>")
        );
      } else {
        notify.error(
          error?.response?.data?.description || "Oops, there was an issue."
        );
      }
      throw error;
    } finally {
      if(toastId)
      notify.dismiss(toastId);
    }
  };

  const login = async (username: string, password: string) => {
    const response = await invokeApi({
      endpoint: "/authenticate",
      data: {
        username,
        password,
        group: params.group,
      },
    });
    setUser(response);
    localStorage.setItem("userState", JSON.stringify(response));
    notify.success("You have successfully logged in!", { id: GLOBAL_TOAST_ID });
    if (location.pathname.includes("login")) {
      navigate(searchParams.get("next") || `/${toSlug(response.group)}`);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    const response = await invokeApi({
      endpoint: "/resetPassword",
      data: { token, password },
    });
    setUser(response);
    localStorage.setItem("userState", JSON.stringify(response));
    notify.success("You have successfully logged in!", {id: GLOBAL_TOAST_ID});
    navigate(`/${toSlug(response.group)}`);
  };

  const logout = () => {
    notify.success("You have successfully logged out");
    navigate(`/${toSlug(user.group)}/login`);
    setUser(initialUserState);
    localStorage.removeItem("userState");
  };

  return {
    login,
    logout,
    resetPassword,
    invokeApi,
    setUser,
    group,
    facility,
    payer: payer?.toUpperCase(),
    authenticated: user?.username,
    isAdmin: user?.type === UserType.Admin,
    user,
    navigate,
    ...user,
  };
};
